import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  DEFAULT_OG_IMAGE_PATH,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_WIDTH,
  getSiteOrigin,
  toAbsoluteUrl,
} from '@/utils/absoluteUrl';

interface SEOHeadProps {
  title: string;
  description?: string;
  keywords?: string[];
  canonicalUrl?: string;
  ogImage?: string;
  /**
   * Set to false when ogImage is an arbitrary asset (an organizer logo, an
   * article hero) rather than a 1200x630 StatHarbour card, so the fixed
   * og:image:width/height are omitted instead of misdeclaring the real size.
   */
  ogImageIsCard?: boolean;
  ogType?: string;
  structuredData?: any;
  twitterCard?: 'summary' | 'summary_large_image';
  /** When true, emit a noindex robots tag (used for demo/preview pages). */
  noindex?: boolean;
  // Legacy props for backward compatibility
  leagueName?: string;
  sport?: string;
  imageUrl?: string;
  leagueData?: {
    name: string;
    sport: string;
    location?: string;
    foundedYear?: number;
    teamsCount?: number;
    currentSeason?: string;
  };
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description = 'Competition operations and an official digital match centre for ambitious semi-professional leagues.',
  keywords = [],
  canonicalUrl,
  ogImage,
  ogImageIsCard = true,
  ogType = 'website',
  structuredData,
  twitterCard = 'summary_large_image',
  noindex = false,
  // Legacy props
  leagueName,
  sport,
  imageUrl,
  leagueData,
}) => {
  const siteUrl = getSiteOrigin();
  const currentUrl = toAbsoluteUrl(
    canonicalUrl || (typeof window !== 'undefined' ? window.location.href : ''),
    siteUrl,
  ) ?? '';
  const fullTitle = leagueName ? `${leagueName} - ${title}` : title;

  // Scrapers require an absolute og:image and will drop a relative one, so the
  // supplied image is always resolved against the current origin. The fallback
  // is the real branded share card: it used to be /placeholder.svg, which made
  // every shared link render as an empty grey box.
  const suppliedImage = toAbsoluteUrl(ogImage || imageUrl, siteUrl);
  const finalImage = suppliedImage ?? toAbsoluteUrl(DEFAULT_OG_IMAGE_PATH, siteUrl)!;

  // Only declare the dimensions when we actually know them, i.e. when the image
  // is one of our own 1200x630 cards. Declaring 1200x630 for an arbitrary
  // organizer logo makes Twitter crop or reject the card.
  const declareImageSize = !suppliedImage || ogImageIsCard;

  // Legacy structured data support
  const legacyStructuredData = leagueData ? {
    "@context": "https://schema.org",
    "@type": "SportsOrganization",
    "name": leagueName || "Sports League",
    "sport": sport,
    "url": currentUrl,
    "description": description,
    "image": finalImage,
    "location": leagueData.location,
    "foundingDate": leagueData.foundedYear?.toString(),
    "numberOfMembers": leagueData.teamsCount,
  } : null;

  const finalStructuredData = structuredData || legacyStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      {sport && !keywords.length && (
        <meta name="keywords" content={`${sport}, league, standings, sports, games, teams, ${leagueName}, schedule, results`} />
      )}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'}
      />
      
      {/* Canonical URL */}
      {currentUrl && <link rel="canonical" href={currentUrl} />}
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:secure_url" content={finalImage} />
      <meta property="og:image:alt" content={fullTitle} />
      {declareImageSize && <meta property="og:image:width" content={String(OG_IMAGE_WIDTH)} />}
      {declareImageSize && <meta property="og:image:height" content={String(OG_IMAGE_HEIGHT)} />}
      <meta property="og:site_name" content="StatHarbour" />
      <meta name="application-name" content="StatHarbour" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={finalImage} />
      <meta name="twitter:image:alt" content={fullTitle} />
      <meta name="twitter:site" content="@statharbour" />
      <meta name="twitter:creator" content="@statharbour" />
      
      {/* Font preconnects (fonts.googleapis.com / fonts.gstatic.com) are
          declared once in index.html, so they are intentionally NOT repeated
          here. Re-adding them via Helmet duplicated the connections and tripped
          Lighthouse's "more than 4 preconnect connections" warning. The unused
          cdnjs dns-prefetch was removed for the same reason (no cdnjs assets). */}

      {/* Article specific tags for league pages */}
      {sport && (
        <>
          <meta property="article:publisher" content="StatHarbour" />
          <meta property="article:section" content="Sports" />
        </>
      )}
      
      {/* Theme color */}
      <meta name="theme-color" content="#2563eb" />
      
      {/* Structured Data (JSON-LD) */}
      {finalStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(finalStructuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
